import type { Dispatch } from "./tracking-dispatch.js";

declare global {
	// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
	interface Window {
		// Ninja Forms depends on jQuery being present as a global, and triggers
		// its submit event with a single { response, id } payload.
		jQuery?: (target: Document) => {
			on: (
				eventName: string,
				handler: (
					event: unknown,
					payload: { id?: unknown; response?: { errors?: unknown } },
				) => void,
			) => void;
		};
	}
}

const ERROR_FIELD_SELECTOR = ".field-wrap.nf-error";
const FIELD_SELECTOR = ".nf-element";
const SUBMIT_SELECTOR = "input[type='submit'], button[type='submit']";
const MILESTONES = [25, 50, 75];
const ERROR_POLL_ATTEMPTS = 10;
const ERROR_POLL_INTERVAL_MS = 150;
const MINUTE_MS = 60000;

/** The form-level facts the tracker needs from its caller. */
export type FormTrackingContext = {
	activeFormIds: string[];
	prefilled: boolean;
};

/**
 * Watches the application form and reports how far applicants get: the first
 * interaction, completion milestones, the fields that fail validation, and the
 * successful submission.
 */
export default class FoundationFormTracker {
	private readonly reachedMilestones = new Set<number>();

	private startedAt = 0;

	/**
	 * @param form The form container rendered by the Foundation shortcode.
	 * @param context The active form IDs and whether a prize arrived
	 *   pre-selected.
	 * @param dispatch Sends the tracked interactions to the analytics vendors.
	 */
	public constructor(
		private readonly form: Element,
		private readonly context: FormTrackingContext,
		private readonly dispatch: Dispatch,
	) {}

	/** Attaches every listener. */
	public watch(): void {
		this.form.addEventListener("focusin", () => {
			this.reportStart();
		});
		this.form.addEventListener("change", () => {
			this.reportProgress();
		});
		this.form.addEventListener("focusout", () => {
			this.reportProgress();
		});
		this.form.addEventListener("click", (event) => {
			if (
				event.target instanceof Element &&
				event.target.closest(SUBMIT_SELECTOR)
			) {
				this.pollForValidationErrors();
			}
		});
		this.watchSubmitResponse();
	}

	/**
	 * Counts the errors in a Ninja Forms submit response, mirroring the `_.size(
	 * response.errors )` test Ninja Forms itself uses to decide whether a
	 * submission succeeded.
	 *
	 * @param errors The response's errors, as a list or an object.
	 * @returns The number of errors reported.
	 */
	private countServerErrors(errors: unknown): number {
		if (Array.isArray(errors)) {
			return errors.length;
		}

		return errors && typeof errors === "object"
			? Object.keys(errors).length
			: 0;
	}

	/**
	 * Buckets the time from the first interaction to submission, since Plausible
	 * breaks properties down by distinct value.
	 *
	 * @returns The bucket label.
	 */
	private durationBucket(): string {
		if (!this.startedAt) {
			return "unknown";
		}

		const minutes = (Date.now() - this.startedAt) / MINUTE_MS;

		if (minutes < 2) {
			return "<2m";
		}

		if (minutes < 5) {
			return "2-5m";
		}

		return minutes < 15 ? "5-15m" : ">15m";
	}

	/**
	 * Names the first field Ninja Forms marked invalid.
	 *
	 * @returns The field's name attribute, or `unknown` when it has none.
	 */
	private firstErroredFieldName(): string {
		const field = this.form.querySelector(
			`${ERROR_FIELD_SELECTOR} ${FIELD_SELECTOR}`,
		);

		return field?.getAttribute("name") ?? "unknown";
	}

	/**
	 * Determines whether a required field has been answered.
	 *
	 * @param field The field to check.
	 * @returns True when the field holds a value.
	 */
	private isFilled(field: HTMLElement): boolean {
		if (field instanceof HTMLInputElement) {
			if (field.type === "radio") {
				return !!this.form.querySelector(
					`input[name="${CSS.escape(field.name)}"]:checked`,
				);
			}

			return field.type === "checkbox"
				? field.checked
				: field.value.trim() !== "";
		}

		return (
			(field instanceof HTMLSelectElement ||
				field instanceof HTMLTextAreaElement) &&
			field.value.trim() !== ""
		);
	}

	/**
	 * Looks for rendered validation errors after a submit attempt. Ninja Forms
	 * validates asynchronously, so the errors appear a moment after the click.
	 *
	 * @param attempt The current attempt, counting up to the poll limit.
	 */
	private pollForValidationErrors(attempt = 0): void {
		const errors = this.form.querySelectorAll(ERROR_FIELD_SELECTOR);

		if (errors.length) {
			this.dispatch("formError", {
				error_count: errors.length,
				field: this.firstErroredFieldName(),
			});

			return;
		}

		if (attempt < ERROR_POLL_ATTEMPTS) {
			window.setTimeout(() => {
				this.pollForValidationErrors(attempt + 1);
			}, ERROR_POLL_INTERVAL_MS);
		}
	}

	/** Reports each completion milestone the applicant reaches, once. */
	private reportProgress(): void {
		const required = this.requiredFields();

		if (!required.length) {
			return;
		}

		const percent =
			(required.filter((field) => this.isFilled(field)).length /
				required.length) *
			100;

		for (const milestone of MILESTONES) {
			if (percent >= milestone && !this.reachedMilestones.has(milestone)) {
				this.reachedMilestones.add(milestone);
				this.dispatch("formProgress", { milestone });
			}
		}
	}

	/** Reports the first interaction with the form, and starts the clock. */
	private reportStart(): void {
		if (this.startedAt) {
			return;
		}

		this.startedAt = Date.now();
		this.dispatch("formStart", {
			prefilled: this.context.prefilled ? "yes" : "no",
		});
	}

	/**
	 * Collects the required fields, counting each radio group once.
	 *
	 * @returns The required fields.
	 */
	private requiredFields(): HTMLElement[] {
		const groups = new Set<string>();

		return [...this.form.querySelectorAll<HTMLElement>("[required]")].filter(
			(field) => {
				if (!(field instanceof HTMLInputElement) || field.type !== "radio") {
					return true;
				}

				if (groups.has(field.name)) {
					return false;
				}

				groups.add(field.name);

				return true;
			},
		);
	}

	/**
	 * Reports the submission itself from Ninja Forms' own response event, which
	 * is the only signal that distinguishes a stored application from a rejected
	 * attempt.
	 *
	 * Ninja Forms triggers this as `jQuery( document ).trigger(
	 * "nfFormSubmitResponse", { response, id } )`, a single payload object rather
	 * than the `( response, formId )` argument pair this handler used to take.
	 */
	private watchSubmitResponse(): void {
		window.jQuery?.(document).on("nfFormSubmitResponse", (_event, payload) => {
			const formId = String(payload.id ?? "");

			if (!this.context.activeFormIds.includes(formId)) {
				return;
			}

			const errorCount = this.countServerErrors(payload.response?.errors);

			if (errorCount) {
				this.dispatch("formError", {
					error_count: errorCount,
					field: "server",
				});

				return;
			}

			this.dispatch("applicationSubmitted", {
				duration: this.durationBucket(),
				form_id: formId,
			});
		});
	}
}
